import config from '../config';

/**
 * Log levels
 */
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Current log level from config
const currentLevel = (() => {
  const configLevel = config.errors.logLevel.toLowerCase();
  switch (configLevel) {
    case 'debug': return LOG_LEVELS.DEBUG;
    case 'info': return LOG_LEVELS.INFO;
    case 'warn': return LOG_LEVELS.WARN;
    case 'error': return LOG_LEVELS.ERROR;
    default: return config.app.isDevelopment ? LOG_LEVELS.DEBUG : LOG_LEVELS.ERROR;
  }
})();

/**
 * Logger class with level-based filtering
 */
class Logger {
  /**
   * Log a debug message
   * @param {string} message - Main message to log
   * @param  {...any} args - Additional arguments to log
   */
  static debug(message, ...args) {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }
  
  /**
   * Log an info message
   * @param {string} message - Main message to log
   * @param  {...any} args - Additional arguments to log
   */
  static info(message, ...args) {
    if (currentLevel <= LOG_LEVELS.INFO) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }
  
  /**
   * Log a warning message
   * @param {string} message - Main message to log
   * @param  {...any} args - Additional arguments to log
   */
  static warn(message, ...args) {
    if (currentLevel <= LOG_LEVELS.WARN) {
      console.warn(`[WARNING] ${message}`, ...args);
    }
  }

  /**
   * Backward-compatible alias for warn()
   * @param {string} message - Main message to log
   * @param  {...any} args - Additional arguments to log
   */
  static warning(message, ...args) {
    Logger.warn(message, ...args);
  }
  
  /**
   * Log an error message
   * @param {string} message - Main message to log
   * @param  {...any} args - Additional arguments to log
   */
  static error(message, ...args) {
    if (currentLevel <= LOG_LEVELS.ERROR) {
      console.error(`[ERROR] ${message}`, ...args);
    }
    
    // Send to error tracking service if enabled and available
    if (config.features.errorTracking && window.errorTrackingService) {
      const errorObj = args.find(arg => arg instanceof Error) || new Error(message);
      window.errorTrackingService.captureException(errorObj, { 
        extra: { args: args.filter(arg => !(arg instanceof Error)) }
      });
    }
  }
  
  /**
   * Log a group of messages
   * @param {string} label - Group label
   * @param {Function} callback - Function containing log calls
   */
  static group(label, callback) {
    console.group(label);
    callback();
    console.groupEnd();
  }
  
  /**
   * Log a collapsible group of messages
   * @param {string} label - Group label
   * @param {Function} callback - Function containing log calls
   */
  static collapsedGroup(label, callback) {
    console.groupCollapsed(label);
    callback();
    console.groupEnd();
  }
  
  /**
   * Measure performance between a start and end point
   * @param {string} label - Label for the measurement
   * @param {Function} callback - Function to measure
   * @returns {*} The result of the callback
   */
  static measure(label, callback) {
    console.time(label);
    const result = callback();
    console.timeEnd(label);
    return result;
  }
  
  /**
   * Log with special styling
   * @param {string} message - Message to log
   * @param {string} style - CSS styling
   */
  static styled(message, style = 'color: blue; font-weight: bold;') {
    console.log(`%c${message}`, style);
  }
}

export default Logger;
